import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import BannerSlider from '../components/BannerSlider';
import { MdShoppingCart, MdLocalShipping, MdSecurity, MdLocalOffer, MdStar } from 'react-icons/md';
import { FaHandsHelping, FaGlobe, FaUsers, FaBoxOpen, FaSmile, FaClock, FaShieldAlt } from 'react-icons/fa';
import CountUp from 'react-countup';
import { useInView } from 'react-intersection-observer';
import { base_url, config } from '../utils/axiosConfig';

const ORANGE = '#ff6701';
const DARK = '#232f3e';
const FONT = '"Rubik", "Poppins", sans-serif';

const About = () => {
  const [about, setAbout] = useState('');
  const [totalProducts, setTotalProducts] = useState(0);
  const [totalCustomers, setTotalCustomers] = useState(0);
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.2 });
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const aboutRes = await axios.get(`${base_url}master/getabout`);
        if (aboutRes.data?.length > 0) setAbout(aboutRes.data[0].name);

        const productsRes = await axios.get(`${base_url}Products`);
        const productsData = Array.isArray(productsRes.data)
          ? productsRes.data
          : productsRes.data?.products || [];
        setTotalProducts(productsData.length);

        const usersRes = await axios.get(`${base_url}user/all-users`, config);
        const usersData = Array.isArray(usersRes.data) ? usersRes.data : [];
        setTotalCustomers(usersData.length);
      } catch (err) {
        console.error('Error fetching about data:', err);
      }
    };
    fetchData();
  }, []);

  return (
    <div style={{ fontFamily: FONT, backgroundColor: '#f5f5f7' }}>

      {/* ── Hero ── */}
      <section style={{ background: `linear-gradient(135deg, ${DARK} 0%, #3b4149 100%)`, padding: '60px 0' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', padding: '0 20px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 40, alignItems: 'center' }}>
            {/* Text */}
            <div>
              <span style={{ fontSize: 12, fontWeight: 700, color: ORANGE, textTransform: 'uppercase', letterSpacing: 2 }}>
                Our Story
              </span>
              <h1 style={{ fontSize: 40, fontWeight: 800, color: '#fff', margin: '12px 0 18px', lineHeight: 1.25 }}>
                About <span style={{ color: ORANGE }}>Shakambari</span>
              </h1>
              <p style={{ fontSize: 15, color: 'rgba(255,255,255,0.75)', lineHeight: 1.8, marginBottom: 28 }}>
                {about || 'We are your trusted eCommerce destination for high-quality products, seamless shopping, and excellent customer support. Built with love for our customers.'}
              </p>
              <div
                onClick={() => navigate('/product')}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8,
                  cursor: 'pointer',
                  backgroundColor: ORANGE, color: '#fff',
                  padding: '13px 28px', borderRadius: 10,
                  fontWeight: 700, fontSize: 14, textDecoration: 'none',
                  boxShadow: '0 6px 20px rgba(255,103,1,0.45)',
                  transition: 'transform 0.2s',
                }}
              >
                <MdShoppingCart size={18} /> Shop Now
              </div>
            </div>
            {/* Banner */}
            <div style={{ borderRadius: 16, overflow: 'hidden'}}>
              <BannerSlider />
            </div>
          </div>
        </div>
      </section>

      {/* ── Stats ── */}
      <section ref={ref} style={{ backgroundColor: '#fff', padding: '50px 20px' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 20 }}>
            {[
              { Icon: FaBoxOpen, color: ORANGE, end: totalProducts, suffix: '+', label: 'Products Available' },
              { Icon: FaSmile, color: '#10b981', end: totalCustomers, suffix: '+', label: 'Happy Customers' },
              { Icon: MdStar, color: '#f59e0b', end: 99, suffix: '%', label: 'Positive Reviews' },
              { Icon: FaClock, color: '#8b5cf6', end: null, label: '24/7 Support', text: '24/7' },
            ].map(({ Icon, color, end, suffix, label, text }) => (
              <div key={label} style={s.statCard}>
                <div style={{ ...s.statIcon, backgroundColor: `${color}18` }}>
                  <Icon size={22} color={color} />
                </div>
                <h2 style={{ fontSize: 30, fontWeight: 800, color: DARK, margin: '12px 0 4px' }}>
                  {text || (inView && end !== null
                    ? <><CountUp end={end} duration={2} />{suffix}</>
                    : '—'
                  )}
                </h2>
                <p style={{ fontSize: 13, color: '#888', margin: 0 }}>{label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Mission / Vision / Values ── */}
      <section style={{ padding: '60px 20px', backgroundColor: '#f5f5f7' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <div style={s.sectionHead}>
            <h2 style={s.sectionTitle}>What Drives Us</h2>
            <p style={s.sectionSub}>Our guiding principles shape every decision we make.</p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 20 }}>
            {[
              { Icon: FaHandsHelping, color: ORANGE, title: 'Our Mission', text: 'To make online shopping simple, fast, and affordable for everyone across India.' },
              { Icon: FaGlobe, color: '#3b82f6', title: 'Our Vision', text: 'To become a trusted global leader in eCommerce powered by innovation and integrity.' },
              { Icon: FaUsers, color: '#10b981', title: 'Our Values', text: 'Customer satisfaction, transparency, and quality service sit at the heart of everything we do.' },
            ].map(({ Icon, color, title, text }) => (
              <div key={title} style={s.mvCard}>
                <div style={{ ...s.mvIcon, backgroundColor: `${color}18` }}>
                  <Icon size={26} color={color} />
                </div>
                <h4 style={{ fontSize: 16, fontWeight: 700, color: DARK, margin: '16px 0 8px' }}>{title}</h4>
                <p style={{ fontSize: 13, color: '#777', lineHeight: 1.7, margin: 0 }}>{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Why Choose Us ── */}
      <section style={{ padding: '60px 20px', backgroundColor: '#fff' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <div style={s.sectionHead}>
            <h2 style={s.sectionTitle}>Why Shop With Us?</h2>
            <p style={s.sectionSub}>We go beyond just selling products — we deliver an experience.</p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16 }}>
            {[
              { Icon: MdLocalShipping, color: '#3b82f6', title: 'Fast Delivery', text: 'Quick and reliable shipping across India with real-time tracking.' },
              { Icon: MdSecurity, color: '#10b981', title: 'Secure Payment', text: 'All transactions encrypted and protected by industry-grade security.' },
              { Icon: MdLocalOffer, color: ORANGE, title: 'Best Deals', text: 'Handpicked offers, seasonal discounts, and unbeatable prices every day.' },
              { Icon: FaShieldAlt, color: '#8b5cf6', title: 'Customer Trust', text: 'Thousands of happy customers trust Shakambari for their daily needs.' },
            ].map(({ Icon, color, title, text }) => (
              <div key={title} style={s.whyCard}>
                <div style={{ ...s.whyIcon, backgroundColor: `${color}12` }}>
                  <Icon size={24} color={color} />
                </div>
                <h5 style={{ fontSize: 14, fontWeight: 700, color: DARK, margin: '14px 0 6px' }}>{title}</h5>
                <p style={{ fontSize: 13, color: '#888', lineHeight: 1.6, margin: 0 }}>{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA Banner ── */}
      <section style={{ background: `linear-gradient(135deg, ${ORANGE} 0%, #febd69 100%)`, padding: '50px 20px', textAlign: 'center' }}>
        <h2 style={{ fontSize: 28, fontWeight: 800, color: '#fff', margin: '0 0 12px' }}>
          Ready to Start Shopping?
        </h2>
        <p style={{ fontSize: 15, color: 'rgba(255,255,255,0.88)', marginBottom: 24 }}>
          Explore thousands of products and discover something you'll love.
        </p>
        <div
          onClick={() => navigate('/product')}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            cursor: 'pointer',
            backgroundColor: '#fff', color: ORANGE,
            padding: '13px 32px', borderRadius: 10,
            fontWeight: 700, fontSize: 14, textDecoration: 'none',
            boxShadow: '0 6px 20px rgba(0,0,0,0.15)',
          }}
        >
          <MdShoppingCart size={16} /> Browse Products
        </div>
      </section>
    </div>
  );
};

const s = {
  sectionHead: { textAlign: 'center', marginBottom: 36 },
  sectionTitle: { fontSize: 26, fontWeight: 800, color: '#1c1c1b', margin: '0 0 8px' },
  sectionSub: { fontSize: 14, color: '#888', margin: 0 },
  statCard: {
    backgroundColor: '#fff', borderRadius: 14, padding: '24px 20px',
    boxShadow: '0 2px 12px rgba(0,0,0,0.06)', textAlign: 'center',
    border: '1px solid #f0f0f0',
  },
  statIcon: {
    width: 48, height: 48, borderRadius: 12,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    margin: '0 auto',
  },
  mvCard: {
    backgroundColor: '#fff', borderRadius: 14, padding: '24px 22px',
    boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
    border: '1px solid #f0f0f0',
  },
  mvIcon: {
    width: 52, height: 52, borderRadius: 14,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  whyCard: {
    padding: '22px 18px', borderRadius: 12,
    border: '1px solid #f0f0f0', backgroundColor: '#fafafa',
  },
  whyIcon: {
    width: 48, height: 48, borderRadius: 12,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
};

export default About;
